import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { toast } from '../../hooks/use-toast';
import { Eye, EyeOff } from 'lucide-react';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockoutTime, setLockoutTime] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reason = searchParams.get('reason');
  const nextPath = searchParams.get('next');

  const MAX_ATTEMPTS = 4;
  const LOCKOUT_DURATION = 60; // 60 seconds lockout

  // Check for existing lockout on mount
  useEffect(() => {
    const storedLockout = localStorage.getItem('adminLockout');
    const storedAttempts = localStorage.getItem('adminFailedAttempts');
    
    if (storedLockout) {
      const lockoutEnd = parseInt(storedLockout);
      const now = Date.now();
      if (lockoutEnd > now) {
        setLockoutTime(lockoutEnd);
        setCountdown(Math.ceil((lockoutEnd - now) / 1000));
      } else {
        localStorage.removeItem('adminLockout');
        localStorage.removeItem('adminFailedAttempts');
      }
    }
    
    if (storedAttempts) {
      setFailedAttempts(parseInt(storedAttempts));
    }
  }, []);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (lockoutTime && countdown === 0) {
      // Lockout expired
      setLockoutTime(null);
      setFailedAttempts(0);
      localStorage.removeItem('adminLockout');
      localStorage.removeItem('adminFailedAttempts');
    }
  }, [countdown, lockoutTime]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if locked out
    if (lockoutTime && Date.now() < lockoutTime) {
      toast({
        title: 'Account Locked',
        description: `Please wait ${countdown} seconds before trying again.`,
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      // Reset attempts on success
      setFailedAttempts(0);
      localStorage.removeItem('adminLockout');
      localStorage.removeItem('adminFailedAttempts');
      
      toast({
        title: 'Success',
        description: 'Logged in successfully!'
      });
      // Respect ?next=... so users return to the page they were on
      const safeNext = nextPath && nextPath.startsWith('/admin') ? nextPath : '/admin/dashboard';
      navigate(safeNext);
    } else {
      const newAttempts = failedAttempts + 1;
      setFailedAttempts(newAttempts);
      localStorage.setItem('adminFailedAttempts', newAttempts.toString());
      
      if (newAttempts >= MAX_ATTEMPTS) {
        // Lock out the user
        const lockoutEnd = Date.now() + (LOCKOUT_DURATION * 1000);
        setLockoutTime(lockoutEnd);
        setCountdown(LOCKOUT_DURATION);
        localStorage.setItem('adminLockout', lockoutEnd.toString());
        
        toast({
          title: 'Account Locked',
          description: `Too many failed attempts. Please wait ${LOCKOUT_DURATION} seconds.`,
          variant: 'destructive'
        });
      } else {
        toast({
          title: 'Error',
          description: `${result.error}. ${MAX_ATTEMPTS - newAttempts} attempts remaining.`,
          variant: 'destructive'
        });
      }
    }

    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!forgotEmail) {
      toast({
        title: 'Error',
        description: 'Please enter your email address.',
        variant: 'destructive'
      });
      return;
    }

    setSendingReset(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      
      // Always show success message to prevent email enumeration
      toast({
        title: 'Email Sent',
        description: 'If an account exists with this email, you will receive password reset instructions.'
      });
      
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      toast({
        title: 'Email Sent',
        description: 'If an account exists with this email, you will receive password reset instructions.'
      });
    }
    
    setSendingReset(false);
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-100 via-teal-50 to-pink-100 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-2xl p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-black mb-2">Reset Password</h1>
              <p className="text-gray-600">Enter your email to receive reset instructions</p>
            </div>

            <form onSubmit={handleForgotPassword} className="space-y-6">
              <div>
                <label className="block text-sm font-bold mb-2">Email Address</label>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-pink-500 focus:outline-none"
                  placeholder="your@email.com"
                />
              </div>

              <Button
                type="submit"
                disabled={sendingReset}
                className="w-full bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white font-bold py-6 rounded-full shadow-lg disabled:opacity-50"
              >
                {sendingReset ? 'Sending...' : 'Send Reset Email'}
              </Button>

              <button
                type="button"
                onClick={() => setShowForgotPassword(false)}
                className="w-full text-gray-600 hover:text-pink-600 font-semibold"
              >
                Back to Login
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-100 via-teal-50 to-pink-100 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="bg-white border-4 border-pink-600 rounded-lg px-4 py-2 inline-block mb-4">
              <div className="text-2xl font-black leading-tight">
                <span className="text-black">1<sup className="text-sm">st</sup> Ave</span>
                <br />
                <span className="text-black">ICP</span>
              </div>
              <div className="text-xs text-teal-600 font-semibold mt-1">
                Ice Cream & Pastry
              </div>
            </div>
            <h1 className="text-3xl font-black mb-2">Admin Login</h1>
            <p className="text-gray-600">Enter your credentials to access the dashboard</p>
          </div>

          {/* Session Expired Banner */}
          {reason === 'session_expired' && (
            <div
              className="bg-amber-50 border-2 border-amber-300 text-amber-800 px-4 py-3 rounded-xl mb-6 text-center"
              data-testid="session-expired-banner"
            >
              <p className="font-bold">Session expired</p>
              <p className="text-sm">Please log in again to continue where you left off.</p>
            </div>
          )}

          {/* Lockout Warning */}
          {lockoutTime && countdown > 0 && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl mb-6 text-center">
              <p className="font-bold">Account Temporarily Locked</p>
              <p className="text-2xl font-black mt-2">{countdown}s</p>
              <p className="text-sm">Please wait before trying again</p>
            </div>
          )}

          {/* Failed Attempts Warning */}
          {failedAttempts > 0 && failedAttempts < MAX_ATTEMPTS && !lockoutTime && (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded-xl mb-6 text-center">
              <p className="font-semibold">{MAX_ATTEMPTS - failedAttempts} attempt(s) remaining</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-bold mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={lockoutTime && countdown > 0}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-pink-500 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="admin@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-bold mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={lockoutTime && countdown > 0}
                  className="w-full px-4 py-3 pr-12 rounded-xl border-2 border-gray-200 focus:border-pink-500 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-pink-500 transition-colors"
                  tabIndex="-1"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || (lockoutTime && countdown > 0)}
              className="w-full bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white font-bold py-6 rounded-full shadow-lg disabled:opacity-50"
            >
              {loading ? 'Logging in...' : lockoutTime && countdown > 0 ? `Locked (${countdown}s)` : 'Login'}
            </Button>

            <Link
              to="/admin/forgot-password"
              className="w-full block text-center text-gray-600 hover:text-pink-600 font-semibold text-sm"
            >
              Forgot Password?
            </Link>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
